import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import { toggleLoader } from '../../../../redux/Slices/Global/globalSlice';
import { useCustomToast } from '../../../Contexts/ToastContext';
import axiosInstance from '../../../../config/Axios';
import ProfileForm from './ProfileForm';
import { readImageSize } from '../../../Apps/Promos/kit/sizeCheck';

const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_BYTES = 2 * 1024 * 1024;

function CompanyProfile() {
    const { t } = useTranslation();
    const companyDetails = useSelector((state) => state.global.companyDetails);
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    // toggleLoader() flips a flag nothing refetches on: re-read /company/details so the sidebar, the promo designer and the renders see the change.
    const refreshCompany = () => queryClient.invalidateQueries('getUserSubscription');
    const { showToast } = useCustomToast();

    const currentLogo = companyDetails?.company?.logo || null;
    const [logoPreview, setLogoPreview] = useState(currentLogo);
    const [uploading, setUploading] = useState(false);
    const [notSquare, setNotSquare] = useState(false);
    const fileInputRef = useRef(null);

    const companyInfo = {
        name: companyDetails?.company?.name,
        phone: companyDetails?.company?.phone,
        email: companyDetails?.company?.email,
        // NO `|| 'Asia/Amman'` here. That default did more than mis-render: it
        // seeded the form with a real value, so an owner whose company had NO
        // timezone would silently WRITE Asia/Amman the first time they saved
        // any unrelated profile field — a Libya venue quietly acquiring Jordan's
        // clock. Unset must stay unset until someone chooses.
        timezone: companyDetails?.company?.timezone || null,
    };

    const handleLogoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!ALLOWED_TYPES.includes(file.type)) {
            showToast('error', t('profile.company_logo_title'), t('profile.logo_invalid_type'));
            return;
        }
        if (file.size > MAX_BYTES) {
            showToast('error', t('profile.company_logo_title'), t('profile.logo_too_large'));
            return;
        }

        // Far from square (aspect ratio outside 0.8-1.25) is almost certainly not a logo: warn, but still allow it.
        try { const sz = await readImageSize(file); const r = sz.w && sz.h ? sz.w / sz.h : 1; setNotSquare(r < 0.8 || r > 1.25); } catch (err) { setNotSquare(false); }
        setLogoPreview(URL.createObjectURL(file));
        setUploading(true);

        const formData = new FormData();
        formData.append('logo', file);

        try {
            const res = await axiosInstance.post('/company/logo', formData);
            setLogoPreview(res.data?.data?.logo_url);
            dispatch(toggleLoader('companyDetails'));
            await refreshCompany();
            showToast('success', t('profile.company_logo_title'), t('profile.logo_upload_success'));
        } catch (err) {
            setLogoPreview(currentLogo);
            showToast('error', t('profile.company_logo_title'), err?.response?.data?.message || t('profile.logo_upload_error'));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleLogoRemove = async () => {
        setUploading(true);
        try {
            await axiosInstance.delete('/company/logo');
            setLogoPreview(null);
            dispatch(toggleLoader('companyDetails'));
            await refreshCompany();
            showToast('success', t('profile.company_logo_title'), t('profile.logo_remove_success'));
        } catch (err) {
            showToast('error', t('profile.company_logo_title'), err?.response?.data?.message);
        } finally {
            setUploading(false);
        }
    };

    const submitHandler = (data) => {
        const formData = new FormData();
        formData.append('name', data.name);
        formData.append('phone', data.phone);
        formData.append('email', data.email);
        if (data.timezone) formData.append('timezone', data.timezone);

        axiosInstance.post(`/company/${companyDetails?.company?.id}/update`, formData)
            .then(() => {
                dispatch(toggleLoader('companyDetails'));
                refreshCompany();
                showToast('success', t('profile.update_company_profile_title'), t('profile.profile_updated_success'));
            })
            .catch((error) => {
                showToast('error', t('profile.update_company_profile_title'), error?.response?.data?.message);
            });
    };

    return (
        <>
            {/* Logo section */}
            <div className="card mb-4">
                <h5 className="card-header text-capitalize">{t('profile.company_logo_title')}</h5>
                <div className="card-body">
                    <div className="d-flex align-items-center gap-4">
                        <div className="flex-shrink-0">
                            {logoPreview
                                ? <img
                                    src={logoPreview}
                                    alt={t('profile.avatar_alt')}
                                    className="d-block rounded"
                                    height={100}
                                    width={100}
                                    style={{ objectFit: 'contain', background: '#f4f4f4' }}
                                  />
                                : <div
                                    className="d-flex align-items-center justify-content-center rounded"
                                    style={{ width: 100, height: 100, background: '#f4f4f4' }}
                                  >
                                    <i className="bx bx-image-alt" style={{ fontSize: 36, color: '#aaa' }} />
                                  </div>
                            }
                        </div>
                        <div className="button-wrapper">
                            <input
                                ref={fileInputRef}
                                type="file"
                                id="company_logo_upload"
                                hidden
                                accept="image/png,image/jpeg,image/webp"
                                onChange={handleLogoChange}
                                disabled={uploading}
                            />
                            <label
                                htmlFor="company_logo_upload"
                                className={`btn btn-primary me-2 mb-2${uploading ? ' disabled' : ''}`}
                                tabIndex={0}
                                style={{ cursor: uploading ? 'wait' : 'pointer' }}
                            >
                                {uploading
                                    ? <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />{t('profile.logo_uploading')}</>
                                    : <><i className="bx bx-upload me-1" />{t('profile.choose_new_photo')}</>
                                }
                            </label>
                            {logoPreview && (
                                <button
                                    type="button"
                                    className="btn btn-outline-danger mb-2"
                                    onClick={handleLogoRemove}
                                    disabled={uploading}
                                >
                                    <i className="bx bx-trash me-1" />{t('profile.logo_remove')}
                                </button>
                            )}
                            <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.8rem' }}>
                                {t('profile.logo_format_hint')}
                            </p>
                            {notSquare && (
                                <p className="mb-0 mt-2 text-warning" role="status" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                    {t('profile.logo_not_square')}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Profile fields: name / email / phone / timezone */}
            <ProfileForm
                profileType="company"
                showImagePicker={false}
                submitHandler={submitHandler}
                data={companyInfo}
            />
        </>
    );
}

export default CompanyProfile;
